import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const parsedPayload = typeof payload === 'string' ? JSON.parse(payload) : payload;
  const response = await fleetApi.post(url + getTzParam(), parsedPayload);
  return response;
};

export const revalidateTireWorkorderList = () => {
  mutate((key) => Array.isArray(key) && key[0].startsWith('/work-order-tire'));
};

// 3. POST /work-order-tire/search
export const useTireWorkorderSearch = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, extraFilters = []) => {
  const isFetchAll = page === undefined;
  const start = isFetchAll ? 0 : page * rowsPerPage;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder()
      .addJoin('company', ['name'])
      .addJoin('vehicle', ['license_plate', 'merk', 'model', 'year', 'name'])
      // .addJoin('tire_batch', ['batch_number'])
      .setPaging(start, rowsPerPage);

    if (extraFilters && extraFilters.length > 0) {
      extraFilters.forEach((f) => {
        if (f.operator && typeof fb[f.operator] === 'function') {
          if (f.operator === 'range') {
            fb.range(f.field, f.value, f.value1, f.logic || 'AND', f.tableName || null);
          } else {
            fb[f.operator](f.field, f.value, f.logic || 'AND', f.tableName || null);
          }
        } else {
          fb.equal(f.field, f.value);
        }
      });
    }

    if (searchKey && searchValue) {
      if (searchKey === 'work_order_number') {
        fb.like('work_order_number', searchValue, 'AND');
        fb.like('work_order_number_internal', searchValue, 'OR');
      } else {
        fb.like(searchKey, searchValue, 'AND');
      }
    }

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    fb.setOrder(['created_at'], false);

    return fb.build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchAll, start, rowsPerPage, searchKey, searchValue, companies, JSON.stringify(extraFilters)]);

  const key = useMemo(() => {
    return payload ? ['/work-order-tire/search', JSON.stringify(payload)] : null;
  }, [payload]);

  const {
    data,
    error,
    isLoading,
    mutate: swrMutate
  } = useSWR(key, fetcher, {
    keepPreviousData: true
  });

  return {
    data: Array.isArray(data?.data?.data) ? data.data.data : Array.isArray(data?.data) ? data.data : [],
    totalRecord: data?.data?.totalRecord || data?.data?.dataSize || data?.totalRecord || 0,
    isLoading,
    error,
    mutate: swrMutate,
    refetch: swrMutate
  };
};

// 4. POST /work-order-tire/new
export const createTireWorkorder = async (payload) => {
  try {
    const response = await fleetApi.post(`/work-order-tire/new${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireWorkorderList();
    return response;
  } catch (error) {
    console.error('Error creating tire work order:', error);
    throw error;
  }
};

// 5. PUT /work-order-tire/edit/{id}
export const updateTireWorkorder = async (id, payload) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/work-order-tire/edit/${id}${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireWorkorderList();
    return response;
  } catch (error) {
    console.error('Error updating tire work order:', error);
    throw error;
  }
};

// 6. DELETE /work-order-tire/delete/{id}
export const deleteTireWorkorder = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/work-order-tire/delete/${id}${getTzParam()}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireWorkorderList();
    return response;
  } catch (error) {
    console.error('Error deleting tire work order:', error);
    throw error;
  }
};

// Special function to change status only (uses PUT /work-order-tire/edit/{id})
export const updateTireWorkorderStatus = async (id, newStatus, notes = null, completeDate = null) => {
  if (id == null) throw new Error('id is required');
  try {
    const payload = { status: newStatus };
    if (notes !== null) {
      payload.notes = notes;
    }
    if (completeDate !== null) {
      payload.complete_date = completeDate;
    }
    // Only sends the status field (and optionally notes/complete_date) for partial update
    const response = await fleetApi.put(`/work-order-tire/edit/${id}${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireWorkorderList();
    return response;
  } catch (error) {
    console.error('Error updating tire work order status:', error);
    throw error;
  }
};

export const exportTireWorkorderSearch = async (selectedCompanies = [], searchKey, searchValue, extraFilters = []) => {
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const fb = new FilterBuilder()
    .addJoin('company', ['name'])
    .addJoin('vehicle', ['license_plate', 'merk', 'model', 'year', 'name'])
    .setPaging(0, 9999);

  if (extraFilters && extraFilters.length > 0) {
    extraFilters.forEach((f) => {
      if (f.operator && typeof fb[f.operator] === 'function') {
        if (f.operator === 'range') {
          fb.range(f.field, f.value, f.value1, f.logic || 'AND', f.tableName || null);
        } else {
          fb[f.operator](f.field, f.value, f.logic || 'AND', f.tableName || null);
        }
      } else {
        fb.equal(f.field, f.value);
      }
    });
  }

  if (searchKey && searchValue) {
    if (searchKey === 'work_order_number') {
      fb.like('work_order_number', searchValue, 'AND');
      fb.like('work_order_number_internal', searchValue, 'OR');
    } else {
      fb.like(searchKey, searchValue, 'AND');
    }
  }

  if (companies) {
    if (companies.includes(',')) {
      fb.in('company_id', companies);
    } else {
      fb.equal('company_id', companies);
    }
  } else {
    fb.filterByCompany();
  }

  fb.setOrder(['created_at'], false);

  try {
    const response = await fleetApi.post(`/work-order-tire/search${getTzParam()}`, fb.build());
    const fetchedData = response?.data?.data || response?.data;
    return Array.isArray(fetchedData) ? fetchedData : [];
  } catch (error) {
    console.error('Error exporting tire work order search:', error);
    return [];
  }
};
